import { readAdminSession } from "./adminRepository";

export type EventStatus = "draft" | "published" | "cancelled" | "completed";
export type PaymentPolicy = "free" | "online_required" | "invoice_required" | "online_or_invoice";
export type PaymentChoice = "online" | "invoice" | "cash" | "not_required";
export type RegistrationStatus = "pending" | "confirmed" | "waitlist" | "cancelled";
export type PaymentStatus = "not_required" | "unpaid" | "pending" | "paid" | "refunded" | "failed";
export type EventEmailTarget = "participant" | "admin" | "both";

export type EventRole = {
  id: string;
  eventId: string;
  label: string;
  description: string;
  capacity: number | null;
  priceCents: number | null;
  currency: string;
  paymentPolicy: PaymentPolicy;
  cancellationTerms: string;
  sortOrder: number;
  isActive: boolean;
  confirmedCount: number;
  waitlistCount: number;
};

export type EventRegistration = {
  id: string;
  eventId: string;
  eventRoleId: string;
  reference: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note: string;
  registrationStatus: RegistrationStatus;
  paymentStatus: PaymentStatus;
  amountCents: number | null;
  currency: string;
  paymentChoice: PaymentChoice;
  paymentMethod: string;
  paymentDueAt: string | null;
  paymentProvider: string;
  paymentReference: string;
  stripeCheckoutSessionId: string;
  stripePaymentIntentId: string;
  createdAt: string;
  confirmedAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  confirmationEmailStatus: string;
  confirmationEmailLastError: string;
  adminNotificationStatus: string;
  adminNotificationLastError: string;
};

export type EventAdminAction = {
  id: string;
  action: string;
  entityId: string;
  actor: string;
  details: Record<string, unknown>;
  createdAt: string;
};

export type BookingEvent = {
  id: string;
  slug: string;
  title: string;
  eyebrow: string;
  summary: string;
  description: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  bookingLocationId: string | null;
  venueName: string;
  addressLine: string;
  postalCode: string;
  city: string;
  countryCode: string;
  registrationOpensAt: string | null;
  registrationClosesAt: string | null;
  status: EventStatus;
  sortOrder: number;
  isActive: boolean;
  roles: EventRole[];
  registrations: EventRegistration[];
};

export type EventAdminSnapshot = {
  events: BookingEvent[];
  actions: EventAdminAction[];
};

export type EventOperationResult = {
  ok: true;
  message: string;
  warning?: string;
  checkoutUrl?: string | null;
};

export type RegistrationDraft = {
  registrationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note: string;
  eventRoleId: string;
};

export type EventDraft = Omit<BookingEvent, "slug" | "roles" | "registrations">;
export type EventRoleDraft = Omit<EventRole, "confirmedCount" | "waitlistCount">;

const EVENTS_ENDPOINT =
  "https://cnejticdknokawqlslsj.supabase.co/functions/v1/booking-admin-events";
const RETRYABLE_EVENT_ADMIN_STATUS = new Set([502, 503, 504]);
const EVENT_ADMIN_RETRY_DELAYS_MS = [350, 900];

export async function loadEventAdminSnapshot(): Promise<EventAdminSnapshot> {
  const session = await requireSession();
  const response = await requestEventAdmin({
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  const payload = await readResponse<{
    events?: Array<Record<string, unknown>>;
    roles?: Array<Record<string, unknown>>;
    registrations?: Array<Record<string, unknown>>;
    actions?: Array<Record<string, unknown>>;
  }>(response);
  const roles = (payload.roles ?? []).map(mapRole);
  const registrations = (payload.registrations ?? []).map(mapRegistration);
  return {
    events: (payload.events ?? []).map((event) => {
      const eventId = String(event.id ?? "");
      return {
        ...mapEvent(event),
        roles: roles.filter((role) => role.eventId === eventId),
        registrations: registrations.filter((registration) => registration.eventId === eventId),
      };
    }),
    actions: (payload.actions ?? []).map(mapAction),
  };
}

export async function saveBookingEvent(event: EventDraft): Promise<string> {
  const result = await command<{ ok: true; id: string }>({ action: "save_event", event });
  return result.id;
}

export async function saveEventRole(role: EventRoleDraft): Promise<string> {
  const result = await command<{ ok: true; id: string }>({ action: "save_role", role });
  return result.id;
}

export async function updateEventRegistration(
  draft: RegistrationDraft,
): Promise<EventOperationResult> {
  return command({ action: "update_registration", ...draft });
}

export async function promoteEventWaitlist(registrationId: string): Promise<EventOperationResult> {
  return command({ action: "promote_waitlist", registrationId });
}

export async function restoreEventRegistration(
  registrationId: string,
): Promise<EventOperationResult> {
  return command({ action: "restore_registration", registrationId });
}

export async function markEventRegistrationPaid(
  registrationId: string,
): Promise<EventOperationResult> {
  return command({ action: "mark_paid", registrationId });
}

export async function markEventRegistrationUnpaid(
  registrationId: string,
): Promise<EventOperationResult> {
  return command({ action: "mark_unpaid", registrationId });
}

export async function cancelEventRegistration(
  registrationId: string,
): Promise<EventOperationResult> {
  return command({ action: "cancel_registration", registrationId });
}

export async function refundEventRegistration(
  registrationId: string,
): Promise<EventOperationResult> {
  return command({ action: "refund_registration", registrationId });
}

export async function markEventRegistrationRefunded(
  registrationId: string,
): Promise<EventOperationResult> {
  return command({ action: "mark_refunded", registrationId });
}

export async function resendEventRegistrationConfirmation(
  registrationId: string,
  target: EventEmailTarget,
): Promise<EventOperationResult> {
  return command({ action: "resend_confirmation", registrationId, target });
}

async function command<T = EventOperationResult>(body: Record<string, unknown>): Promise<T> {
  const session = await requireSession();
  const response = await requestEventAdmin({
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return readResponse<T>(response);
}

async function requestEventAdmin(init: RequestInit): Promise<Response> {
  for (let attempt = 0; attempt <= EVENT_ADMIN_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const response = await fetch(EVENTS_ENDPOINT, init);
      const shouldRetry =
        RETRYABLE_EVENT_ADMIN_STATUS.has(response.status) &&
        attempt < EVENT_ADMIN_RETRY_DELAYS_MS.length;
      if (!shouldRetry) return response;
    } catch (error) {
      if (attempt >= EVENT_ADMIN_RETRY_DELAYS_MS.length) {
        console.error("event_admin_network_failed", error);
        throw new Error(
          "Die Veranstaltungsverwaltung war kurz nicht erreichbar. Bitte noch einmal versuchen.",
        );
      }
    }
    await wait(EVENT_ADMIN_RETRY_DELAYS_MS[attempt] ?? 0);
  }
  throw new Error(
    "Die Veranstaltungsverwaltung war kurz nicht erreichbar. Bitte noch einmal versuchen.",
  );
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function requireSession() {
  const session = await readAdminSession();
  if (!session) throw new Error("Die Anmeldung ist abgelaufen. Bitte erneut anmelden.");
  return session;
}

async function readResponse<T = unknown>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(errorMessage(payload.error));
  return payload;
}

function mapEvent(value: Record<string, unknown>): Omit<BookingEvent, "roles" | "registrations"> {
  return {
    id: String(value.id ?? ""),
    slug: String(value.slug ?? ""),
    title: String(value.title ?? ""),
    eyebrow: String(value.eyebrow ?? ""),
    summary: String(value.summary ?? ""),
    description: String(value.description ?? ""),
    startsAt: String(value.starts_at ?? ""),
    endsAt: String(value.ends_at ?? ""),
    timezone: String(value.timezone ?? "Europe/Berlin"),
    bookingLocationId: value.booking_location_id ? String(value.booking_location_id) : null,
    venueName: String(value.venue_name ?? ""),
    addressLine: String(value.address_line ?? ""),
    postalCode: String(value.postal_code ?? ""),
    city: String(value.city ?? ""),
    countryCode: String(value.country_code ?? "DE"),
    registrationOpensAt: value.registration_opens_at ? String(value.registration_opens_at) : null,
    registrationClosesAt: value.registration_closes_at
      ? String(value.registration_closes_at)
      : null,
    status: isEventStatus(value.status) ? value.status : "draft",
    sortOrder: Number(value.sort_order ?? 0),
    isActive: Boolean(value.is_active),
  };
}

function mapRole(value: Record<string, unknown>): EventRole {
  return {
    id: String(value.id ?? ""),
    eventId: String(value.event_id ?? ""),
    label: String(value.label ?? ""),
    description: String(value.description ?? ""),
    capacity: nullableNumber(value.capacity),
    priceCents: nullableNumber(value.price_cents),
    currency: String(value.currency ?? "EUR"),
    paymentPolicy: isPaymentPolicy(value.payment_policy)
      ? value.payment_policy
      : "online_or_invoice",
    cancellationTerms: String(value.cancellation_terms ?? ""),
    sortOrder: Number(value.sort_order ?? 0),
    isActive: Boolean(value.is_active),
    confirmedCount: Number(value.confirmedCount ?? 0),
    waitlistCount: Number(value.waitlistCount ?? 0),
  };
}

function mapRegistration(value: Record<string, unknown>): EventRegistration {
  return {
    id: String(value.id ?? ""),
    eventId: String(value.event_id ?? ""),
    eventRoleId: String(value.event_role_id ?? ""),
    reference: String(value.reference ?? ""),
    firstName: String(value.first_name ?? ""),
    lastName: String(value.last_name ?? ""),
    email: String(value.email ?? ""),
    phone: String(value.phone ?? ""),
    note: String(value.note ?? ""),
    registrationStatus: isRegistrationStatus(value.registration_status)
      ? value.registration_status
      : "pending",
    paymentStatus: isPaymentStatus(value.payment_status) ? value.payment_status : "unpaid",
    amountCents: nullableNumber(value.amount_cents),
    currency: String(value.currency ?? "EUR"),
    paymentChoice: isPaymentChoice(value.payment_choice) ? value.payment_choice : "not_required",
    paymentMethod: String(value.payment_method ?? ""),
    paymentDueAt: value.payment_due_at ? String(value.payment_due_at) : null,
    paymentProvider: String(value.payment_provider ?? ""),
    paymentReference: String(value.payment_reference ?? ""),
    stripeCheckoutSessionId: String(value.stripe_checkout_session_id ?? ""),
    stripePaymentIntentId: String(value.stripe_payment_intent_id ?? ""),
    createdAt: String(value.created_at ?? ""),
    confirmedAt: value.confirmed_at ? String(value.confirmed_at) : null,
    paidAt: value.paid_at ? String(value.paid_at) : null,
    cancelledAt: value.cancelled_at ? String(value.cancelled_at) : null,
    confirmationEmailStatus: String(value.confirmation_email_status ?? ""),
    confirmationEmailLastError: String(value.confirmation_email_last_error ?? ""),
    adminNotificationStatus: String(value.admin_notification_status ?? ""),
    adminNotificationLastError: String(value.admin_notification_last_error ?? ""),
  };
}

function mapAction(value: Record<string, unknown>): EventAdminAction {
  const details = value.details;
  return {
    id: String(value.id ?? ""),
    action: String(value.action ?? ""),
    entityId: String(value.entity_id ?? ""),
    actor: String(value.actor ?? ""),
    details:
      details && typeof details === "object" && !Array.isArray(details)
        ? (details as Record<string, unknown>)
        : {},
    createdAt: String(value.created_at ?? ""),
  };
}

function nullableNumber(value: unknown): number | null {
  return value === null || value === undefined ? null : Number(value);
}

function isEventStatus(value: unknown): value is EventStatus {
  return ["draft", "published", "cancelled", "completed"].includes(String(value));
}

function isPaymentPolicy(value: unknown): value is PaymentPolicy {
  return ["free", "online_required", "invoice_required", "online_or_invoice"].includes(
    String(value),
  );
}

function isPaymentChoice(value: unknown): value is PaymentChoice {
  return ["online", "invoice", "cash", "not_required"].includes(String(value));
}

function isRegistrationStatus(value: unknown): value is RegistrationStatus {
  return ["pending", "confirmed", "waitlist", "cancelled"].includes(String(value));
}

function isPaymentStatus(value: unknown): value is PaymentStatus {
  return ["not_required", "unpaid", "pending", "paid", "refunded", "failed"].includes(
    String(value),
  );
}

function errorMessage(error?: string): string {
  const messages: Record<string, string> = {
    event_title_required: "Bitte einen Titel für die Veranstaltung eintragen.",
    invalid_event_time: "Bitte Beginn und Ende der Veranstaltung prüfen.",
    event_role_label_required: "Bitte die Teilnahmeform benennen.",
    invalid_event_capacity: "Die Kapazität muss leer oder größer als null sein.",
    invalid_event_price: "Der Preis darf nicht negativ sein.",
    invalid_registration_name: "Bitte Vor- und Nachnamen prüfen.",
    invalid_registration_email: "Bitte eine gültige E-Mail-Adresse eintragen.",
    invalid_registration_phone: "Die Telefonnummer ist zu lang.",
    invalid_registration_note: "Die Nachricht ist zu lang.",
    registration_role_locked:
      "Bei bezahlten oder erstatteten Buchungen kann die Teilnahmeform nicht direkt geändert werden.",
    registration_not_waitlisted: "Diese Anmeldung steht nicht auf der Warteliste.",
    registration_not_cancelled: "Diese Anmeldung ist nicht storniert.",
    registration_cancelled:
      "Eine stornierte Anmeldung kann nicht als bezahlt markiert werden. Bitte zuerst wiederherstellen.",
    registration_already_cancelled: "Diese Anmeldung ist bereits storniert.",
    registration_already_refunded: "Diese Zahlung wurde bereits als erstattet markiert.",
    registration_not_confirmed:
      "Eine Abschlussbestätigung kann nur für einen bestätigten Platz versendet werden.",
    registration_payment_not_complete:
      "Die Abschlussbestätigung kann erst nach abgeschlossener Zahlung versendet werden.",
    event_role_full:
      "Diese Teilnahmeform ist bereits voll. Der Wartelistenplatz wurde nicht bestätigt.",
    stripe_payment_requires_refund:
      "Eine abgeschlossene Stripe-Zahlung muss über die Erstattungsfunktion korrigiert werden.",
    payment_not_refundable: "Für diese Anmeldung ist aktuell keine Erstattung möglich.",
    stripe_payment_not_found: "Die zugehörige Stripe-Zahlung wurde nicht eindeutig gefunden.",
    stripe_refund_failed:
      "Stripe konnte die Erstattung nicht auslösen. Es wurde nichts am Buchungsstatus geändert.",
    stripe_checkout_failed: "Die neue Zahlungsseite konnte nicht erstellt werden.",
    invalid_email_target: "Das Ziel der Bestätigungsmail ist ungültig.",
    authentication_required: "Die Anmeldung ist abgelaufen. Bitte erneut anmelden.",
    admin_access_denied: "Für diesen Verwaltungsbereich fehlt die Freigabe.",
  };
  return (
    (error && messages[error]) || "Die Änderung konnte gerade nicht vollständig ausgeführt werden."
  );
}
